// Which kinds of food a recipe belongs to, for the "what do you enjoy eating" answers. Decided from
// the recipe's own ingredients, cuisine and region, never guessed.

import { judgeIngredients } from './diet';
import type { FoodLike } from './preferences';
import { getRecipe, RECIPES, type Recipe } from './recipes';
import { recipeImage } from './recipeImages';
import { RECIPE_META } from './recipeMeta';
import { INGREDIENTS, lineage, type IngredientId } from './taxonomy';

const has = (recipe: Recipe, root: IngredientId) => recipe.ingredients.some((i) => i.role !== 'optional' && lineage(i.id).includes(root));
const NOODLES = new Set<IngredientId>(['egg_noodles', 'rice_noodles', 'ramen_noodles', 'udon_noodles']);

/** Food groups a recipe belongs to. */
export function foodGroups(recipe: Recipe): Set<FoodLike> {
  const out = new Set<FoodLike>();
  const meta = RECIPE_META[recipe.id];
  const category = getRecipe(recipe.id)?.category;
  const core = recipe.ingredients.filter((i) => i.role !== 'optional').map((i) => i.id);
  const meats = core.map((id) => INGREDIENTS[id].meat).filter(Boolean);
  if (meats.length) out.add('meat');
  if (core.some((id) => INGREDIENTS[id].category === 'protein' && INGREDIENTS[id].allergens?.some((a) => a === 'fish' || a === 'shellfish'))) out.add('seafood');
  if (has(recipe, 'rice')) out.add('rice');
  if (has(recipe, 'pasta') || core.some((id) => NOODLES.has(id))) out.add('pasta_noodles');
  if (!judgeIngredients(core, ['vegetarian']).blocked) out.add('vegetarian');
  if (category === 'east_asian' || category === 'south_southeast_asian') out.add('asian');
  if (category === 'middle_east_north_africa' || meta?.cuisine === 'mediterranean' || meta?.cuisine === 'italian') out.add('mediterranean');
  if (category === 'african_caribbean') out.add('african_caribbean');
  if (category === 'american' || meta?.cuisine === 'british') out.add('comfort');
  if (category === 'latin_american' || category === 'european' || category === 'international') out.add('international');
  return out;
}

/** The user's liked groups this recipe matches, in the order they were listed. */
export function matchedLikes(recipe: Recipe, likes: FoodLike[]): FoodLike[] {
  const groups = foodGroups(recipe);
  return likes.filter((l) => l !== 'unsure' && groups.has(l));
}

/** Dishes that show each group well, tried in order; falls back to any match with a photo. */
const SHOWCASE: Partial<Record<FoodLike, string[]>> = {
  meat: ['classic-cheeseburgers', 'honey-garlic-chicken-thighs'],
  rice: ['chicken-biryani', 'chicken-and-egg-fried-rice'],
  pasta_noodles: ['spaghetti-aglio-e-olio', 'chicken-lo-mein'],
  seafood: ['sheet-pan-salmon-potatoes', 'lemon-garlic-shrimp'],
  vegetarian: ['caprese-salad', 'black-bean-tacos'],
  asian: ['chicken-lo-mein', 'thai-red-curry-vegetables'],
  mediterranean: ['greek-lamb-meatballs', 'caprese-salad'],
  comfort: ['loaded-baked-potatoes', 'chicken-and-dumplings'],
  international: ['tamale-pie', 'breakfast-burritos'],
};

/** A real recipe photo for a food group, or null when the catalog has none (never a stand-in). */
export function showcaseRecipe(like: FoodLike): string | null {
  const ok = (id: string) => !!getRecipe(id) && !!recipeImage(id) && foodGroups(getRecipe(id)!).has(like);
  return (SHOWCASE[like] ?? []).find(ok) ?? RECIPES.find((r) => ok(r.id))?.id ?? null;
}
